import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, MessageCircle, Zap, Clock } from "lucide-react";
import heroComponents from "@/assets/optimized/hero-components.jpg";
import heroPackages from "@/assets/optimized/hero-packages.jpg";
import heroSystems from "@/assets/optimized/hero-systems.jpg";
import { useLang } from "@/i18n/LanguageContext";
import type { TKey } from "@/i18n/translations";
import { cn } from "@/lib/utils";
import { whatsappLinkProps } from "@/lib/whatsapp";
import { trackCtaClick } from "@/lib/analytics";

/**
 * Hero carousel — aligned with the 3-layer product architecture.
 * Slide order reflects strategic weighting: Components (core, LCP),
 * Packages (expansion), Systems (credibility).
 */
type Slide = { src: string; altKey: TKey; captionKey: TKey };
const slides: Slide[] = [
  {
    src: heroComponents,
    altKey: "hero.slide.components.alt",
    captionKey: "hero.slide.components.caption",
  },
  {
    src: heroPackages,
    altKey: "hero.slide.packages.alt",
    captionKey: "hero.slide.packages.caption",
  },
  {
    src: heroSystems,
    altKey: "hero.slide.systems.alt",
    captionKey: "hero.slide.systems.caption",
  },
];

const ROTATION_MS = 4500;

const Hero = () => {
  const { t, localePath } = useLang();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const id = window.setInterval(
      () => setActive((i) => (i + 1) % slides.length),
      ROTATION_MS,
    );
    return () => window.clearInterval(id);
  }, [paused]);

  const bulletKeys: TKey[] = [
    "hero.bullet1",
    "hero.bullet2",
    "hero.bullet3",
    "hero.bullet4",
  ];

  return (
    <section className="relative overflow-hidden border-b border-border bg-background">
      <div className="container grid items-center gap-10 py-10 md:py-20 lg:grid-cols-2 lg:gap-16 lg:py-28">
        {/* Left: copy */}
        <div className="max-w-2xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
            <Zap className="h-3.5 w-3.5" />
            {t("hero.badge")}
          </div>

          <h1 className="text-[28px] font-bold leading-[1.15] tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-6xl">
            {t("hero.h1.part1")}{" "}
            <span className="text-primary">{t("hero.h1.part2")}</span>
          </h1>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:hidden">
            {t("hero.subtitle.mobile")}
          </p>
          <p className="mt-4 hidden text-base leading-relaxed text-muted-foreground sm:block sm:text-lg">
            {t("hero.subtitle")}
          </p>
          <div className="mt-4 hidden items-center gap-2 rounded-lg border border-border/60 bg-secondary/50 px-4 py-2.5 sm:flex">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("hero.systemLine.label")}
            </span>
            <span className="h-3 w-px bg-border" />
            <span className="text-sm font-medium text-foreground">
              {t("hero.systemLine.desktop")}
            </span>
          </div>

          {/* Differentiation line */}
          <p className="mt-4 text-sm font-semibold text-foreground sm:text-base">
            {t("hero.differentiator")}
          </p>

          {/* Speed emphasis — above CTAs. Neutral chip, blue icon only,
              so the primary CTA below remains the strongest visual element. */}
          <p className="mt-4 inline-flex items-center gap-2 rounded-md border border-border bg-secondary px-3 py-1.5 text-sm font-semibold text-foreground sm:text-base">
            <Clock className="h-4 w-4 text-primary" />
            {t("hero.speedLine")}
          </p>

          {/* Best for — qualifies fit in one glance */}
          <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm">
            <span className="font-semibold text-foreground">{t("hero.bestFor.label")}</span>
            <span className="inline-flex items-center rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-foreground">
              {t("hero.bestFor.1")}
            </span>
            <span className="inline-flex items-center rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-foreground">
              {t("hero.bestFor.2")}
            </span>
            <span className="inline-flex items-center rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-foreground">
              {t("hero.bestFor.3")}
            </span>
          </div>

          {/* CTAs — above the fold */}
          <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
            <Button asChild size="lg" className="h-12 w-full font-semibold sm:w-auto">
              <Link to={localePath("/request-quote")} onClick={() => trackCtaClick("hero", "request_quote")}>
                {t("cta.getQuoteHours")}
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="whatsapp"
              className="h-12 w-full font-semibold sm:w-auto"
            >
              <a {...whatsappLinkProps("hero")}>
                <MessageCircle className="mr-2 h-4 w-4" />
                {t("cta.sendWhatsapp")}
              </a>
            </Button>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
            {t("cta.responseExpectation")}
          </p>
          {/* Trust line — directly under CTAs */}
          <p className="mt-3 text-sm font-medium text-muted-foreground">
            {t("hero.trustLine")}
          </p>

          {/* Bullets */}
          <ul className="mt-6 grid gap-2.5 sm:grid-cols-2 sm:gap-3">
            {bulletKeys.map((k) => (
              <li
                key={k}
                className="flex items-start gap-2.5 text-sm text-foreground"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                <span className="font-medium">{t(k)}</span>
              </li>
            ))}
          </ul>

          {/* Highlight */}
          <div className="mt-6 flex items-center gap-3 rounded-lg bg-secondary px-4 py-3">
            <Clock className="h-5 w-5 shrink-0 text-foreground" />
            <p className="text-sm font-medium text-foreground">
              {t("hero.delivery")}{" "}
              <span className="font-bold">{t("hero.deliveryRange")}</span>
            </p>
          </div>
        </div>

        {/* Right: image carousel */}
        <div
          className="relative lg:pl-4"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-tr from-secondary to-transparent" />
          <div
            className="relative aspect-square overflow-hidden rounded-2xl border border-border shadow-xl"
            role="region"
            aria-roledescription="carousel"
            aria-label="Switchgear categories we source"
          >
            {slides.map((s, i) => (
              <img
                key={s.src}
                src={s.src}
                alt={t(s.altKey)}
                width={800}
                height={800}
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : "auto"}
                decoding={i === 0 ? "sync" : "async"}
                aria-hidden={i === active ? "false" : "true"}
                className={cn(
                  "absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out motion-reduce:transition-none",
                  i === active ? "opacity-100" : "opacity-0",
                )}
              />
            ))}

            {/* Caption — positioned top so it never overlaps the bottom-left "< 4 hours" stat card */}
            <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-foreground/75 via-foreground/35 to-transparent px-4 pb-10 pt-4 sm:px-5 sm:pt-5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-background/80 sm:text-xs">
                {t("hero.carousel.eyebrow")}
              </p>
              <p className="mt-1 text-sm font-bold leading-snug text-background sm:text-base">
                {t(slides[active].captionKey)}
              </p>
            </div>

            {/* Speed badge overlay */}
            <div className="absolute left-3 bottom-12 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-[11px] font-bold text-primary-foreground shadow-lg sm:left-4 sm:bottom-14 sm:text-xs">
              <Clock className="h-3.5 w-3.5" />
              {t("hero.imageBadge")}
            </div>

            {/* Dots */}
            <div className="absolute bottom-3 right-3 flex gap-1.5 sm:bottom-4 sm:right-4">
              {slides.map((s, i) => (
                <button
                  key={s.src}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`Show slide ${i + 1}: ${t(s.captionKey)}`}
                  aria-current={i === active}
                  className={cn(
                    "h-1.5 rounded-full bg-background/70 transition-all duration-300",
                    i === active ? "w-6 bg-background" : "w-1.5 hover:bg-background",
                  )}
                />
              ))}
            </div>
          </div>
          {/* Floating stat card */}
          <div className="absolute -bottom-6 left-6 hidden rounded-xl border border-border bg-background p-4 shadow-xl sm:block lg:left-0">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("hero.avgResponse")}
            </p>
            <p className="mt-1 text-2xl font-bold text-foreground">
              {t("hero.avgResponseValue")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
